import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Scan, ArrowRight, Eye, Calendar, Search, Loader2, Settings, Clock } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  all: 'Todo',
  tutorial: 'Tutorial',
  news: 'Noticias IA',
  'use-case': 'Casos de uso',
  update: 'Novedades',
};

const CATEGORY_COLORS = {
  tutorial: 'bg-blue-500/15 text-blue-400 border-blue-500/25',
  news: 'bg-violet-500/15 text-violet-400 border-violet-500/25',
  'use-case': 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
  update: 'bg-amber-500/15 text-amber-400 border-amber-500/25',
};

// ─── Post card ─────────────────────────────────────────────────────────────────
function PostCard({ post, onClick, index }) {
  const color = CATEGORY_COLORS[post.category] || 'bg-zinc-700/20 text-zinc-400 border-zinc-700/30';
  const date = new Date(post.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  const readTime = Math.max(1, Math.ceil((post.content || post.excerpt || '').split(/\s+/).length / 200));

  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06, duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      onClick={onClick}
      className="group relative bg-zinc-900/60 border border-zinc-800/80 hover:border-zinc-700 rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-black/40 flex flex-col"
    >
      {/* Cover image or emoji */}
      <div className="h-32 sm:h-36 flex items-center justify-center bg-gradient-to-b from-zinc-800/60 to-zinc-900/80 border-b border-zinc-800/60 group-hover:from-zinc-800 transition-colors duration-300 flex-shrink-0 overflow-hidden">
        {post.cover_image
          ? <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" loading="lazy" />
          : <span className="text-6xl">{post.cover_emoji || '📝'}</span>
        }
      </div>

      <div className="p-5 flex flex-col flex-1 gap-3">
        {/* Badges */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[11px] font-mono px-2 py-0.5 rounded-full border font-medium ${color}`}>
            {CATEGORY_LABELS[post.category] || post.category}
          </span>
          {post.featured && (
            <span className="text-[11px] font-mono px-2 py-0.5 rounded-full border bg-amber-500/15 text-amber-400 border-amber-500/25">
              ⭐ Destacado
            </span>
          )}
          {!post.published && (
            <span className="text-[11px] font-mono px-2 py-0.5 rounded-full border bg-red-500/15 text-red-400 border-red-500/25">
              Borrador
            </span>
          )}
        </div>

        {/* Title */}
        <h2 className="font-heading text-base sm:text-lg font-bold text-white group-hover:text-blue-400 transition-colors leading-snug line-clamp-2">
          {post.title}
        </h2>

        {/* Excerpt */}
        <p className="text-zinc-500 text-sm leading-relaxed line-clamp-2 flex-1">
          {post.excerpt}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 border-t border-zinc-800/60">
          <div className="flex items-center gap-3 text-zinc-600 text-xs font-mono">
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />{date}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />{readTime} min
            </span>
            <span className="flex items-center gap-1">
              <Eye className="w-3 h-3" />{post.views || 0}
            </span>
          </div>
          <ArrowRight className="w-4 h-4 text-zinc-700 group-hover:text-blue-400 group-hover:translate-x-1 transition-all duration-200" />
        </div>
      </div>
    </motion.article>
  );
}

// ─── Featured post (big card) ─────────────────────────────────────────────────
function FeaturedCard({ post, onClick }) {
  const color = CATEGORY_COLORS[post.category] || 'bg-zinc-700/20 text-zinc-400 border-zinc-700/30';
  const date = new Date(post.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });
  const readTime = Math.max(1, Math.ceil((post.content || post.excerpt || '').split(/\s+/).length / 200));

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      onClick={onClick}
      className="group relative col-span-full bg-gradient-to-br from-zinc-900 to-zinc-900/60 border border-zinc-800/80 hover:border-zinc-700 rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-black/50"
    >
      <div className="flex flex-col sm:flex-row">
        {/* Cover image or emoji side */}
        <div className="sm:w-48 h-36 sm:h-auto flex items-center justify-center bg-gradient-to-br from-zinc-800/80 to-zinc-900 border-b sm:border-b-0 sm:border-r border-zinc-800/60 flex-shrink-0 group-hover:from-zinc-800 transition-colors duration-300 overflow-hidden">
          {post.cover_image
            ? <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" loading="lazy" />
            : <span className="text-7xl sm:text-8xl">{post.cover_emoji || '📝'}</span>
          }
        </div>
        <div className="p-6 sm:p-8 flex flex-col justify-center gap-3 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-[11px] font-mono px-2.5 py-1 rounded-full border font-medium ${color}`}>
              {CATEGORY_LABELS[post.category] || post.category}
            </span>
            <span className="text-[11px] font-mono px-2.5 py-1 rounded-full border bg-amber-500/15 text-amber-400 border-amber-500/25">
              ⭐ Destacado
            </span>
          </div>
          <h2 className="font-heading text-xl sm:text-2xl md:text-3xl font-black text-white group-hover:text-blue-400 transition-colors leading-snug">
            {post.title}
          </h2>
          <p className="text-zinc-400 text-sm sm:text-base leading-relaxed line-clamp-2">{post.excerpt}</p>
          <div className="flex items-center gap-4 text-zinc-600 text-xs font-mono">
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{date}</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{readTime} min</span>
            <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{post.views || 0}</span>
            <ArrowRight className="w-4 h-4 ml-auto text-zinc-700 group-hover:text-blue-400 group-hover:translate-x-1 transition-all" />
          </div>
        </div>
      </div>
    </motion.article>
  );
}

// ─── Main Blog page ────────────────────────────────────────────────────────────
export default function Blog({ onBack, onPost, user, onNavigateAdmin }) {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('veritas_token');
    if (token) {
      axios.get(`${API_URL}/api/blog/admin/check`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setIsAdmin(r.data.is_admin))
        .catch(() => {});
    }
  }, [user]);

  useEffect(() => { fetchPosts(); }, [category, page]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('veritas_token');
      const params = { page, limit: 12, ...(category !== 'all' && { category }) };
      const res = await axios.get(`${API_URL}/api/blog/posts`, {
        params,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setPosts(res.data.posts);
      setTotalPages(res.data.pages);
    } catch {
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const filtered = search.trim()
    ? posts.filter(p =>
        p.title.toLowerCase().includes(search.toLowerCase()) ||
        p.excerpt.toLowerCase().includes(search.toLowerCase()) ||
        (p.tags || []).some(t => t.toLowerCase().includes(search.toLowerCase()))
      )
    : posts;

  const featured = filtered.filter(p => p.featured);
  const regular = filtered.filter(p => !p.featured);

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Ambient */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full opacity-[0.05]"
          style={{ background: 'radial-gradient(ellipse, #3b82f6, transparent 70%)' }} />
      </div>

      {/* ── NAVBAR ── */}
      <header className="sticky top-0 z-50 border-b border-white/[0.06] bg-zinc-950/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-zinc-400 hover:text-white transition-colors font-mono text-xs sm:text-sm group"
          >
            <ArrowRight className="w-3.5 h-3.5 rotate-180 group-hover:-translate-x-0.5 transition-transform" />
            <span className="hidden sm:inline">Volver</span>
          </button>

          <div className="w-px h-4 bg-zinc-800" />

          <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <Scan className="w-4 h-4 text-blue-500" />
            <span className="font-heading text-sm font-black tracking-wide">
              VERITAS <span className="text-blue-500">SCAN</span>
            </span>
          </button>

          {isAdmin && (
            <button
              onClick={onNavigateAdmin}
              className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800/80 hover:bg-zinc-700 border border-zinc-700/80 text-zinc-300 font-mono text-xs rounded-lg transition-all"
            >
              <Settings className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Admin</span>
            </button>
          )}
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">

        {/* ── HERO ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-10 sm:mb-14"
        >
          <p className="font-mono text-xs tracking-[0.2em] text-blue-500 uppercase mb-3">Blog</p>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-black tracking-tight text-white mb-4 leading-[1.05]">
            Aprende sobre IA
          </h1>
          <p className="text-zinc-400 text-base sm:text-lg max-w-lg leading-relaxed">
            Tutoriales, noticias y casos de uso sobre detección de contenido generado por inteligencia artificial.
          </p>
        </motion.div>

        {/* ── FILTERS ── */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8 sm:mb-10">
          {/* Search */}
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar artículos..."
              className="w-full bg-zinc-900/80 border border-zinc-800 rounded-xl pl-10 pr-4 py-2.5 text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-zinc-600 font-mono text-sm transition-colors"
            />
          </div>

          {/* Category tabs — scrollable on mobile */}
          <div className="flex gap-1 bg-zinc-900/60 border border-zinc-800 rounded-xl p-1 overflow-x-auto scrollbar-hide flex-shrink-0">
            {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
              <button
                key={key}
                onClick={() => { setCategory(key); setPage(1); }}
                className={`px-3 py-1.5 rounded-lg font-mono text-xs whitespace-nowrap transition-all ${
                  category === key
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-zinc-500 hover:text-zinc-300'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ── CONTENT ── */}
        {loading ? (
          <div className="flex items-center justify-center py-28">
            <Loader2 className="w-7 h-7 text-blue-500 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="text-center py-28 space-y-4"
          >
            <p className="text-5xl">📭</p>
            <p className="font-heading text-xl text-white">No hay artículos todavía</p>
            <p className="text-zinc-500 text-sm font-mono">Vuelve pronto — estamos preparando contenido</p>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {filtered.map((post, i) => (
              <PostCard key={post.id} post={post} index={i} onClick={() => onPost(post.slug)} />
            ))}
          </div>
        )}

        {/* ── PAGINATION ── */}
        {totalPages > 1 && !search && (
          <div className="flex items-center justify-center gap-3 mt-12">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 font-mono text-sm border border-zinc-700 rounded-xl text-zinc-400 hover:text-white hover:border-zinc-500 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              ← Anterior
            </button>
            <span className="font-mono text-sm text-zinc-500">{page} / {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 font-mono text-sm border border-zinc-700 rounded-xl text-zinc-400 hover:text-white hover:border-zinc-500 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              Siguiente →
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
